import classNames from 'classnames';
import React, { CSSProperties, useMemo } from 'react';
import { ButtonGroup, Checkbox, CheckboxGroup, Dropdown, InputNumber } from 'rsuite';
import { Tag, TagProps } from '../../../../../../components/Tag';
import { useCompetitorFiltersStore } from '../../../../../../store/useCompetitorFiltersStore';
import { CompetitorPriceSourcesDataList, ICompetitorPriceSources, removeUnavailableCompetitorPriceSources } from '../../../../Entities/CompetitorPriceSources';
import styles from './FilterSelectInterval.module.scss';
import { IconTypes } from './Icon';
import { IconButton } from './IconButton';

type FilterSelectIntervalProps = {
  icon?: IconTypes | React.ReactNode;
  prefixLabel?: string;
  unitLabel?: string;
  menuStyle: CSSProperties;
  skin?: 'neutral' | 'primary' | 'error';
  onClose?: () => void;
  maxValue?: number;
  defaultValue?: string;
  placeholder?: string;
  onClick?: (event: React.SyntheticEvent<Element, Event>) => void;
  onChange?: (value: string | number, event?: React.SyntheticEvent<any, Event> | undefined) => void;

  onSelect?: (eventKey: any, event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  competitorPriceSources: {
    value: ICompetitorPriceSources[];
    onChange?: (value: ICompetitorPriceSources[]) => void;
  };
  inputValue?: string;
};

const TAGS: Record<ICompetitorPriceSources, (props: TagProps) => React.JSX.Element> = {
  MPDV: props => <Tag {...props}>MPDV</Tag>,
  INFOPANEL: props => <Tag {...props}>INFOPANEL</Tag>,
  PROPRIA: props => (
    <Tag {...props} skin="gray" className={styles['the-gray-one']}>
      EXTERNO
    </Tag>
  ),
};

export const FilterSelectInterval = ({
  icon = 'MdSelectAll',
  prefixLabel,
  unitLabel,
  menuStyle,
  placeholder,
  skin = 'neutral',
  onSelect,
  onClick,
  onClose,
  onChange,
  maxValue,
  inputValue,
  defaultValue,
  competitorPriceSources,
}: FilterSelectIntervalProps) => {
  const { competitorFilters } = useCompetitorFiltersStore();

  const competitorPriceSourcesValue = useMemo(() => {
    const competitorPriceSourcesFilter = competitorFilters.find(filter => filter.filterType === 'competitorPriceSources');

    if (!competitorPriceSourcesFilter) {
      return removeUnavailableCompetitorPriceSources(competitorPriceSources.value);
    }

    const savedValues = removeUnavailableCompetitorPriceSources(competitorPriceSourcesFilter.filterValue);
    const currentValues = removeUnavailableCompetitorPriceSources(competitorPriceSources.value);

    return [...new Set([...savedValues, ...currentValues])];
  }, [competitorFilters, competitorPriceSources.value]);

  const handleCheckboxChange = (value: ICompetitorPriceSources[]) => {
    const competitorPriceSourcesFilter = competitorFilters.find(filter => filter.filterType === 'competitorPriceSources');

    if (competitorPriceSourcesFilter) {
      const savedValues = removeUnavailableCompetitorPriceSources(competitorPriceSourcesFilter.filterValue);
      if (value.length > savedValues.length) {
        const newValue = [...new Set([...savedValues, ...value])];
        competitorPriceSources.onChange?.(newValue);
      } else {
        const newValue = savedValues.filter((v: ICompetitorPriceSources) => value.includes(v));
        competitorPriceSources.onChange?.(newValue);
      }
    } else {
      competitorPriceSources.onChange?.(value);
    }
  };

  return (
    <ButtonGroup className={classNames('quick-action-filter__button__container quick-action-filter__toggle', styles.container)} data-testid="filter-select">
      <IconButton icon={icon} placeholder={placeholder} skin={skin} onClick={onClick} data-testid="placeholder-icon" />
      <Dropdown
        activeKey="1"
        eventKey="1"
        menuStyle={menuStyle}
        onSelect={onSelect}
        placement="bottomEnd"
        onClose={onClose}
        renderTitle={() => <IconButton icon="MdKeyboardArrowDown" className="quick-action-filter__toggle__arrow" skin={skin} />}
        className={styles['dropdown-container']}
      >
        <Dropdown.Item panel className="quick-action-filter__select-interval">
          <div className="quick-action-filter__select-interval__dropdown-item">
            <p>{prefixLabel}</p>
            <InputNumber
              min={0}
              max={maxValue}
              value={Number(inputValue)}
              onChange={value => {
                if (value !== undefined) {
                  onChange?.(value);
                }
              }}
            />
            <p>{unitLabel}</p>
          </div>
        </Dropdown.Item>

        <Dropdown.Item panel>
          <CheckboxGroup name="competitorPriceSources" value={competitorPriceSourcesValue} onChange={handleCheckboxChange}>
            <p>Origem do preço concorrente:</p>
            {CompetitorPriceSourcesDataList.map(item => {
              const RenderTag = TAGS[item.value];
              return (
                <div key={item.value} className={styles['flex-item']}>
                  <Checkbox value={item.value}>{item.label}</Checkbox>
                  <RenderTag skin="blue" size="small" outlined />
                </div>
              );
            })}
          </CheckboxGroup>
        </Dropdown.Item>
      </Dropdown>
    </ButtonGroup>
  );
};
